import { cast, flow, getRoot, getType, Instance, isStateTreeNode, types } from 'mobx-state-tree'
import { GameApi } from '../../services/api-objects'
import { Awaited } from '../../utility-types'
import { GameQuiz, isGameQuestion, isInfoCard } from '../game/GameQuiz'
import { subscriptionTutorial, tutorial } from '../game/Tutorial'
import { DateTime } from 'luxon'
import { sortBy } from 'lodash'
import { Factoid } from '../Factoid'

export const isReviewInfoCard = (item: any): item is Instance<typeof ReviewInfoCard> => {
  return item && isStateTreeNode(item) && getType(item).name === 'ReviewInfoCard'
}

export const ReviewInfoCard = types.model('ReviewInfoCard').props({
  card_id: types.identifierNumber,
  card_title: types.string,
  order: types.maybeNull(types.number),
})

export const QuestionAnswerChoiceItem = types.model('QuestionAnswerChoiceItem').props({
  choice_id: types.identifierNumber,
  pairs: types.maybeNull(types.array(types.model({ left: types.string, right: types.string }))),
  ordering: types.maybeNull(types.array(types.model({ text: types.string, index: types.number }))),
  choice_text: types.maybeNull(types.string),
  correct: types.maybeNull(types.boolean),
})

export const ReviewQuestion = types.model('ReviewQuestion').props({
  factoid: types.maybeNull(Factoid),
  question_text: types.string,
  question_type: types.enumeration([
    'Ordering',
    'Open Text',
    'Matching',
    'Multiple Choice',
    'True or False',
    'Multiple Response',
  ]),
  correct: types.boolean,
  image_url: types.maybeNull(types.string),
  question_answer_choices: types.array(QuestionAnswerChoiceItem),
  user_answer_choices: types.maybeNull(types.array(types.model({ choice_id: types.number }))),
  order: types.maybeNull(types.number),
})

export const GameQuizReview = types.model('GameQuizReview').props({
  status: types.optional(
    types.enumeration(['idle', 'pending', 'pending-adaptive', 'done', 'error', 'ready']),
    'idle',
  ),
  assignment_id: types.maybeNull(types.number),
  medal_level: types.maybeNull(types.string),
  title: types.string,
  items: types.array(types.union(ReviewQuestion, ReviewInfoCard)),
  total_correct: types.number,
  total_questions: types.number,
  total_points: types.number,
  points_gained: types.number,
  user_photo_url: types.string,
  pre_test_score: types.maybeNull(types.number),
  pre_test_total_correct: types.maybeNull(types.number),
  post_test_score: types.maybeNull(types.number),
  is_reinforcement: types.boolean,
  is_assessment: types.boolean,
  reinforcement_topic_id: types.maybeNull(types.number),
})

export const GameStoreModel = types
  .model('GameStore')
  .props({
    status: types.optional(types.enumeration(['idle', 'pending', 'done', 'error']), 'idle'),
    quiz: types.maybe(GameQuiz),
    review: types.maybe(GameQuizReview),
  })
  .actions((self) => ({
    setStatus(value?: 'idle' | 'pending' | 'done' | 'error') {
      self.status = value || 'idle'
    },
    setReview(review) {
      self.review = review
    },
    setQuiz(data: typeof self.quiz) {
      self.quiz = data
    },
    start(quiz) {
      this.setQuiz(undefined)
      const items = sortBy(quiz.items, ['order'])
      this.setQuiz(
        cast({
          ...quiz,
          items,
          title: quiz.quiz.title,
          id: quiz.quiz.id,
        }),
      )
      self.quiz?.setActiveItem(self.quiz.items[0])
      if (isGameQuestion(self.quiz?.activeItem)) {
        self.quiz?.activeItem?.setStartTime(Date.now())
      } else if (isInfoCard(self.quiz?.activeItem)) {
        self.quiz?.activeItem?.setStartTime(DateTime.now().toUTC().toISO())
      }
      self.status = 'done'
    },
  }))
  .views((self) => ({
    get loading() {
      return self.status === 'pending'
    },
    get rootStore(): any {
      return getRoot(self)
    },
  }))
  .actions((self) => ({
    startTutorial: (subscription?: boolean) => {
      self.setStatus('done')
      self.start(subscription ? subscriptionTutorial : tutorial)
    },
    startQuiz: flow(function* (id: number, metadata) {
      self.setStatus('pending')
      try {
        const result: Awaited<ReturnType<typeof GameApi.startQuiz>> = yield GameApi.startQuiz(
          id,
          metadata,
        )
        if (result.ok) {
          self.start(result.data)
        } else {
          self.setStatus('error')
        }
      } catch {
        self.setStatus('error')
      }
    }),
    startAssessment: flow(function* (id: number, metadata) {
      self.setStatus('pending')
      try {
        const result: Awaited<ReturnType<typeof GameApi.startAssessment>> =
          yield GameApi.startAssessment(id, metadata)
        if (result.ok) {
          self.start(result.data)
        }
      } catch {
        self.setStatus('error')
      }
    }),
    startReinforcement: flow(function* (topic_id?: number) {
      self.setStatus('pending')
      try {
        const result: Awaited<ReturnType<typeof GameApi.startReinforcement>> =
          yield GameApi.startReinforcement(topic_id)
        if (result.ok) {
          self.start({ ...result.data, reinforcement: true })
        } else {
          self.setStatus('error')
        }
      } catch {
        self.setStatus('error')
      }
    }),
    getReview: flow(function* (result_id: number, user_id: number, topic_id: any) {
      self.setStatus('pending')
      self.setReview(undefined)
      try {
        const result = yield GameApi.getReview(result_id, user_id, topic_id)
        if (result.ok) {
          self.setReview(result.data)
          self.setStatus('done')
          return result.data
        } else {
          self.setStatus('error')
          return { error: true }
        }
      } catch (e) {
        self.setStatus('error')
        return { error: true }
      }
    }),
  }))
